/**
 * Dev-only tool inspector (enable with ?debug=1). Lists registered tools,
 * lets you edit JSON input, run the tool, and see the bounded result.
 * Runs through the same wrapped execute the agent would hit (ledger, undo, etc.).
 */
import { getModelContext } from '../webmcp/context';
import { registry, type JSONSchema } from '../webmcp/register';

export function inspectorEnabled(): boolean {
  return new URLSearchParams(location.search).get('debug') === '1';
}

function sampleFor(schema: JSONSchema | undefined): unknown {
  if (!schema) return {};
  switch (schema.type) {
    case 'string':
      return schema.enum?.[0] ?? '';
    case 'number':
    case 'integer':
      return 0;
    case 'boolean':
      return false;
    case 'array':
      return [];
    case 'null':
      return null;
    case 'object':
    default: {
      const o: Record<string, unknown> = {};
      for (const [k, p] of Object.entries(schema.properties ?? {})) o[k] = sampleFor(p);
      return o;
    }
  }
}

interface ListedTool {
  name: string;
  description?: string;
  inputSchema?: JSONSchema;
  origin: 'native' | 'local';
}

async function listTools(): Promise<ListedTool[]> {
  const mc = getModelContext();
  if (mc?.getTools) {
    try {
      const raw = await mc.getTools();
      if (Array.isArray(raw)) {
        // Chrome 152 returns inputSchema as a JSON string.
        const parse = (v: unknown): JSONSchema | undefined => {
          if (typeof v !== 'string') return v as JSONSchema | undefined;
          try { return JSON.parse(v) as JSONSchema; } catch { return undefined; }
        };
        return raw.map((t: any) => ({ name: t.name, description: t.description, inputSchema: parse(t.inputSchema), origin: 'native' }));
      }
    } catch (e) {
      console.warn('[inspector] mc.getTools() failed, falling back to local registry', e);
    }
  }
  return registry.list().map((t) => ({ name: t.def.name, description: t.def.description, inputSchema: t.def.inputSchema, origin: 'local' }));
}

export function mountInspector(container: HTMLElement = document.body): HTMLElement {
  const root = document.createElement('aside');
  root.className = 'wm-inspector';
  root.setAttribute('aria-label', 'Tool inspector');
  root.innerHTML = `
    <div class="wm-inspector__row"><strong>Tool inspector</strong><span class="muted" data-src></span></div>
    <select data-tool aria-label="Tool"></select>
    <div class="wm-inspector__desc" data-desc></div>
    <textarea class="mono" data-input aria-label="JSON input" spellcheck="false"></textarea>
    <div class="wm-inspector__row">
      <button type="button" class="btn btn--primary btn--small" data-run>Run</button>
      <button type="button" class="btn btn--small" data-refresh>Refresh tools</button>
      <span class="muted" data-status></span>
    </div>
    <pre class="mono" data-result></pre>`;

  const sel = root.querySelector<HTMLSelectElement>('[data-tool]')!;
  const desc = root.querySelector<HTMLElement>('[data-desc]')!;
  const input = root.querySelector<HTMLTextAreaElement>('[data-input]')!;
  const result = root.querySelector<HTMLPreElement>('[data-result]')!;
  const status = root.querySelector<HTMLElement>('[data-status]')!;
  const src = root.querySelector<HTMLElement>('[data-src]')!;
  let tools: ListedTool[] = [];

  const showTool = () => {
    const t = tools.find((x) => x.name === sel.value);
    desc.textContent = t?.description ?? '';
    input.value = JSON.stringify(sampleFor(t?.inputSchema), null, 2);
  };

  const refresh = async () => {
    tools = await listTools();
    const prev = sel.value;
    sel.replaceChildren(...tools.map((t) => Object.assign(document.createElement('option'), { value: t.name, textContent: t.name })));
    src.textContent = tools.length ? `${tools.length} · ${tools[0].origin}` : 'no tools';
    if (tools.some((t) => t.name === prev)) sel.value = prev;
    showTool();
  };

  sel.addEventListener('change', showTool);
  root.querySelector('[data-refresh]')!.addEventListener('click', refresh);
  root.querySelector('[data-run]')!.addEventListener('click', async () => {
    let parsed: unknown;
    try {
      parsed = input.value.trim() ? JSON.parse(input.value) : {};
    } catch (e) {
      result.textContent = `Invalid JSON: ${(e as Error).message}`;
      return;
    }
    status.textContent = 'running…';
    const t0 = performance.now();
    const out = await registry.invoke(sel.value, parsed, 'inspector');
    status.textContent = `${Math.round(performance.now() - t0)} ms`;
    result.textContent = typeof out === 'string' ? out : JSON.stringify(out, null, 2);
  });

  registry.subscribe(() => void refresh());
  getModelContext()?.addEventListener?.('toolchange', () => void refresh());
  void refresh();
  container.append(root);
  return root;
}
